import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import useUser from '@/hooks/useUser'
import { useGetOutboundCallQuery, useGetVoiceNumbersQuery, useHangupOutboundCallMutation, usePlaceOutboundCallMutation, type VoiceCall, type VoicePhoneNumber } from '@/state/operator/operator.slice'
import { useSoftphone, type SoftphoneState } from '@/components/EnquiriesWorkspace/calls/use-softphone'

/**
 * One phone call for the whole app. The call lives here, at the root, so it
 * survives closing the dialog and moving around the product: minimise it and
 * it becomes a pill at the bottom of the screen; open the pill and the dialog
 * comes back with the same call. Only Hang up ends it.
 *
 * Two ways to carry your half of the call. "Ring my phone" has the venue's
 * number ring you first and, when you answer, dial the guest and bridge.
 * "From this browser" makes the dashboard the phone (sip.js). Either way the
 * guest sees the venue's number and the call is on the venue's record.
 */

export type CallMode = 'ring_me' | 'browser'
export type CallTarget = { org_id: string; serial: string; enquiry: { id: string; name: string; phone: string } }

const RING_ME_KEY = 'switch.ring_me_number'
const readRingMe = (): string => {
  try {
    return localStorage.getItem(RING_ME_KEY) ?? ''
  } catch {
    return ''
  }
}
const saveRingMe = (value: string) => {
  try {
    localStorage.setItem(RING_ME_KEY, value)
  } catch {
    // per-browser convenience only
  }
}
export const clock = (secs: number) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
const refusal = (err: unknown): string => {
  const data = (err as { data?: { error?: unknown } } | undefined)?.data
  return typeof data?.error === 'string' ? data.error : 'The call could not be placed.'
}

export type CallSession = {
  target: CallTarget | null
  dialogOpen: boolean
  mode: CallMode
  setMode: (m: CallMode) => void
  staffNumber: string
  setStaffNumber: (n: string) => void
  /** the venue's number, once loaded; null when it has none */
  number: VoicePhoneNumber | null
  numbersLoading: boolean
  softphone: ReturnType<typeof useSoftphone>
  ringMe: VoiceCall | null
  ringMeOver: boolean
  liveSeconds: number
  placing: boolean
  hangingUp: boolean
  /** a call is being placed or is up */
  busy: boolean
  /** the guest is on the line (either mode) */
  live: boolean
  status: string | null
  softphoneState: SoftphoneState
  /** open the dialog for this guest (a new target while nothing is up; the same call otherwise) */
  openCall: (target: CallTarget) => void
  showDialog: () => void
  minimise: () => void
  /** close the dialog; when nothing is up any more, forget the target */
  closeDialog: () => void
  start: () => Promise<void>
  hangUp: () => Promise<void>
}

const Ctx = createContext<CallSession | null>(null)

export function CallSessionProvider({ children }: { children: ReactNode }) {
  const user = useUser()
  const [target, setTarget] = useState<CallTarget | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [mode, setMode] = useState<CallMode>('ring_me')
  const [staffNumber, setStaffNumber] = useState(readRingMe)
  const [place, { isLoading: placing }] = usePlaceOutboundCallMutation()
  const [hangupRingMe, { isLoading: hangingUp }] = useHangupOutboundCallMutation()
  const [ringMeCallId, setRingMeCallId] = useState<string | null>(null)
  const softphone = useSoftphone()

  const { data: numbers, isLoading: numbersLoading } = useGetVoiceNumbersQuery({ org_id: target?.org_id ?? '', serial: target?.serial ?? '' }, { skip: !target })
  const number = useMemo(() => numbers?.phone_numbers.find((n) => n.status === 'active' && n.provider_number_sid) ?? null, [numbers])

  // ring-me: watch the row until the call is over
  const { data: liveRow } = useGetOutboundCallQuery({ call_id: ringMeCallId ?? '' }, { skip: !ringMeCallId, pollingInterval: 2000 })
  const ringMe: VoiceCall | null = liveRow?.call ?? null
  const ringMeOver = Boolean(ringMe?.ended_at)
  const [liveSeconds, setLiveSeconds] = useState(0)
  useEffect(() => {
    if (!ringMe?.answered_at || ringMe.ended_at) return undefined
    const answered = new Date(ringMe.answered_at).getTime()
    const tick = () => setLiveSeconds(Math.max(0, Math.round((Date.now() - answered) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [ringMe?.answered_at, ringMe?.ended_at])

  const browserUp = mode === 'browser' && softphone.state !== 'idle' && softphone.state !== 'failed'
  const ringMeUp = mode === 'ring_me' && ringMeCallId !== null && !ringMeOver
  const busy = placing || browserUp || ringMeUp
  const live = (mode === 'browser' && softphone.state === 'live') || (mode === 'ring_me' && !!ringMe?.answered_at && !ringMe.ended_at)

  const openCall = useCallback(
    (next: CallTarget) => {
      if (busy && target && target.enquiry.id !== next.enquiry.id) {
        toast(`You are still on a call with ${target.enquiry.name}. Hang up before calling someone else.`)
        setDialogOpen(true)
        return
      }
      if (!busy) {
        setTarget(next)
        setRingMeCallId(null)
        setLiveSeconds(0)
      }
      setDialogOpen(true)
    },
    [busy, target]
  )
  const showDialog = useCallback(() => setDialogOpen(true), [])
  const minimise = useCallback(() => setDialogOpen(false), [])
  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    if (!busy) {
      setTarget(null)
      setRingMeCallId(null)
      setLiveSeconds(0)
    }
  }, [busy])

  const start = useCallback(async () => {
    if (!target || !number) return
    if (mode === 'ring_me') {
      const staff = staffNumber.trim()
      if (!staff) {
        toast.error('Enter the number to ring you on.')
        return
      }
      saveRingMe(staff)
    }
    try {
      const result = await place({ org_id: target.org_id, serial: target.serial, mode, to: target.enquiry.phone, ...(mode === 'ring_me' ? { staff_number: staffNumber.trim() } : {}), enquiry_id: target.enquiry.id }).unwrap()
      if (mode === 'browser') {
        if (!result.token || !result.dial) throw new Error('no call token')
        await softphone.dial({ to: result.dial, token: result.token, identity: `connect-${user?.uid ?? 'staff'}` })
      } else {
        setRingMeCallId(result.call.id)
      }
    } catch (err) {
      toast.error(refusal(err))
    }
  }, [target, number, mode, staffNumber, place, softphone, user?.uid])

  const hangUp = useCallback(async () => {
    if (mode === 'browser') await softphone.hangUp()
    else if (ringMeCallId) {
      try {
        await hangupRingMe({ call_id: ringMeCallId }).unwrap()
      } catch {
        toast.error('Could not end the call from here. Hang up on your phone.')
      }
    }
  }, [mode, softphone, ringMeCallId, hangupRingMe])

  let status: string | null = null
  if (mode === 'browser') {
    status = { idle: null, connecting: 'Connecting', ringing: 'Ringing the guest', live: `Connected · ${clock(softphone.seconds)}`, ending: 'Ending', failed: softphone.failure ?? 'The call could not be placed.' }[softphone.state]
  } else if (ringMe) {
    if (ringMe.ended_at) status = ringMe.answered_at ? `Ended · ${clock(ringMe.duration_secs ?? liveSeconds)}` : 'Nobody answered'
    else if (ringMe.answered_at) status = `Connected · ${clock(liveSeconds)}`
    else status = 'Ringing your phone, then the guest'
  } else if (ringMeCallId) status = 'Placing the call'

  const value = useMemo<CallSession>(
    () => ({ target, dialogOpen, mode, setMode, staffNumber, setStaffNumber, number, numbersLoading, softphone, ringMe, ringMeOver, liveSeconds, placing, hangingUp, busy, live, status, softphoneState: softphone.state, openCall, showDialog, minimise, closeDialog, start, hangUp }),
    [target, dialogOpen, mode, staffNumber, number, numbersLoading, softphone, ringMe, ringMeOver, liveSeconds, placing, hangingUp, busy, live, status, openCall, showDialog, minimise, closeDialog, start, hangUp]
  )
  return (
    <Ctx.Provider value={value}>
      {children}
      {/* the browser leg's audio lives with the session, never with a dialog that can unmount */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={softphone.audioRef} autoPlay />
    </Ctx.Provider>
  )
}

export function useCallSession(): CallSession {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useCallSession needs a CallSessionProvider above it')
  return ctx
}

/** Where the provider is not mounted (tests, isolated stories) callers can check first. */
export function useOptionalCallSession(): CallSession | null {
  return useContext(Ctx)
}
